import AssignmentIcon from "@mui/icons-material/Assignment";
import GroupIcon from "@mui/icons-material/Group";
import { Grid, Paper, styled, Typography } from "@mui/material";
import { api, types } from "api";
import graduate from "assets/icons/graduate.svg";
import { StudentGroupList } from "./StudentGroupList";
import { InfoSnackBar } from "components/InfoSnackBar";
import { Assignment, getStatus, Status } from "helpers/assignment";
import { ACTIVITIES_FETCH_ERR } from "messages";
import { routes } from "routes";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

const PREFIX = "StudentGroupPage";

const classes = {
	root: `${PREFIX}-root`,
	button: `${PREFIX}-button`,
	title: `${PREFIX}-title`,
	groupList: `${PREFIX}-groupList`,
	summaryCard: `${PREFIX}-summaryCard`,
	summaryTitle: `${PREFIX}-summaryTitle`,
	summaryText: `${PREFIX}-summaryText`,
	icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: 20,
	},

	[`& .${classes.button}`]: {
		marginTop: 15,
		marginRight: 5,
		float: "right",
	},

	[`& .${classes.title}`]: {
		marginTop: 5,
		marginBottom: 15,
		float: "left",
	},

	[`& .${classes.groupList}`]: {
		maxHeight: 200,
		padding: 5,
		overflowY: "auto",
		overflowX: "hidden",
		float: "left",
	},

	[`& .${classes.summaryCard}`]: {
		marginTop: 30,
		height: 200,
		padding: theme.spacing(2),
		minWidth: 200,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(2),
	},

	[`& .${classes.summaryTitle}`]: {
		color: theme.palette.text.secondary,
		textAlign: "center",
		fontSize: 16,
		paddingTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.summaryText}`]: {
		fontSize: 16,
		textAlign: "center",
		fontWeight: "bold",
		marginTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.icon}`]: {
		color: theme.palette.warning.main,
	},
}));

export const StudentGroupPage = (): JSX.Element => {
	const [assignments, setAssignment] = useState<Assignment[]>([]);
	const [groups, setGroups] = useState<types.Group[]>([]);
	const [err, setErr] = useState(false);
	const [groupsNum, setGroupsNum] = useState(0);

	const componentMounted = useRef(true);
	const history = useHistory();

	// Open group management page
	const handleViewGroup = (groupId: string): void => {
		history.push(routes._.groups._.view.path.replace(":groupId", groupId));
	};

	useEffect(() => {
		api
			.getAssignments()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					const now = new Date();
					setAssignment(
						(res.data || []).map((ass) => ({
							...ass,
							status: getStatus(ass, now),
						})),
					);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getGroups()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					setGroups(res.data || []);
					if (res.data !== null) {
						setGroupsNum(res.data.length);
					}
				}
			})
			.catch(() => {
				setErr(true);
			});
		return () => {
			componentMounted.current = false;
		};
	}, []);

	const stateCount = (state: Status): number => {
		let count = 0;
		assignments.forEach((el) => {
			if (el.status === state) {
				count += 1;
			}
		});
		return count;
	};

	return (
		<Root className={classes.root}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h2" data-testid="hello">
						My Groups
					</Typography>
				</Grid>
				<Grid item xs={5}>
					<Grid container spacing={2}>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								TOTAL GROUPS
							</Typography>
							<Typography className={classes.summaryText}>
								{groupsNum} groups
							</Typography>
							<Grid container justifyContent="space-around">
								<GroupIcon
									data-testid="groups"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								ACTIVE ASSIGNMENTS
							</Typography>
							<Typography className={classes.summaryText}>
								{stateCount("In Progress")} assignments
							</Typography>
							<Grid container justifyContent="space-around">
								<AssignmentIcon
									data-testid="assignments"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
					</Grid>
				</Grid>
				<Grid item xs={6}>
					<img
						data-testid="graduate"
						src={graduate}
						alt="graduate"
						style={{ maxHeight: 300, maxWidth: 500 }}
					/>
				</Grid>

				<Grid item xs={12}>
					{groups.length === 0 ? (
						<Typography variant="h5" className={classes.title}>
							No groups
						</Typography>
					) : (
						<StudentGroupList groups={groups} handleClick={handleViewGroup} />
					)}
				</Grid>
			</Grid>

			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
